import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import BasicButton from "../../components/basics/BasicButton";
import {
  HOME_BANNER_MAX_LENGTH,
  useGetHomeBannerQuery,
  useSetHomeBannerMutation,
} from "./homeBannerApi";
import { rememberHomeBanner } from "./useHomeBanner";

// The home page's two announcement boxes, editable in place at the top of /admin. Each box is
// drawn like its twin on the home page (BoxDivImportant / BoxDivImportant2 in home.js), so what
// you type here is what everyone sees there. An empty box is hidden on the home page.

function errMsg(e: unknown, fallback: string): string {
  const data = (e as { data?: { message?: string } })?.data;
  return (data && typeof data.message === "string" && data.message) || fallback;
}

const AutoGrowText: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  placeholder: string;
  disabled: boolean;
  label: string;
}> = ({ value, onChange, onSave, placeholder, disabled, label }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <BoxInput
      ref={ref}
      rows={1}
      value={value}
      aria-label={label}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={HOME_BANNER_MAX_LENGTH}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          onSave();
        }
      }}
    />
  );
};

const HomeBannerSettings: React.FC = () => {
  const { data, isLoading, isError } = useGetHomeBannerQuery();
  const [save, { isLoading: saving }] = useSetHomeBannerMutation();
  const [text1, setText1] = useState("");
  const [text2, setText2] = useState("");

  useEffect(() => {
    if (data) {
      setText1(data.text1);
      setText2(data.text2);
    }
  }, [data]);

  const dirty = !!data && (data.text1 !== text1.trim() || data.text2 !== text2.trim());

  const onSave = async () => {
    if (!dirty || saving) return;
    try {
      const saved = await save({ text1, text2 }).unwrap();
      // so the home page in this browser shows the new text straight away
      rememberHomeBanner(saved);
      toast.success("home banner saved");
    } catch (e) {
      toast.error(errMsg(e, "could not save the home banner"));
    }
  };

  const onRevert = () => {
    if (!data) return;
    setText1(data.text1);
    setText2(data.text2);
  };

  const locked = isLoading || isError || saving;

  return (
    <Wrap>
      <Title>home banner</Title>
      <Hint>
        {isError
          ? "could not load the banner. refresh to try again."
          : "the boxes on top of the home page. leave one empty to hide it."}
      </Hint>

      <OrangeBox>
        <AutoGrowText
          label="orange box"
          value={text1}
          onChange={setText1}
          onSave={onSave}
          placeholder={isLoading ? "..." : "orange box (empty = hidden)"}
          disabled={locked}
        />
      </OrangeBox>

      <PinkBox>
        <AutoGrowText
          label="pink box"
          value={text2}
          onChange={setText2}
          onSave={onSave}
          placeholder={isLoading ? "..." : "pink box (empty = hidden)"}
          disabled={locked}
        />
      </PinkBox>

      <Actions>
        <BasicButton
          text={saving ? "saving..." : "save"}
          onClick={onSave}
          disabled={!dirty || locked}
          fontWeight="700"
        />
        {dirty && !saving && (
          <BasicButton text="undo" onClick={onRevert} bgColor="#fff" hoverBgColor="#f1f1f1" />
        )}
      </Actions>
    </Wrap>
  );
};

export default HomeBannerSettings;

const Wrap = styled.section`
  width: 100%;
  max-width: 1100px;
  box-sizing: border-box;
  margin-bottom: 8px;
`;

const Title = styled.h2`
  margin: 4px 20px 0;
`;

const Hint = styled.p`
  margin: 2px 20px 0;
  font-size: 0.9rem;
  color: #3d4a3f;
`;

/* same box as BoxDivImportant in home.js */
const OrangeBox = styled.div`
  border: 4px solid;
  padding: 5px;
  padding-left: 10px;
  box-shadow: 0px 0px 10px 2px rgba(255, 111, 0, 0.907), 5px 10px rgba(0, 0, 0, 0.7);
  background-color: #ff611dbc;
  margin: 20px;
  font-weight: 700;
  font-size: 1.5rem;
`;

/* same box as BoxDivImportant2 in home.js */
const PinkBox = styled.div`
  border: 4px solid;
  padding: 5px;
  padding-left: 10px;
  box-shadow: 0px 0px 10px 2px rgba(255, 0, 230, 0.907), 5px 10px rgba(0, 0, 0, 0.7);
  background-color: #ff1dc3bc;
  margin: 20px;
  font-weight: 700;
  font-size: 1.5rem;
`;

const BoxInput = styled.textarea`
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: inherit;
  resize: none;
  overflow: hidden;
  outline: none;

  &::placeholder {
    color: rgba(0, 0, 0, 0.45);
  }

  &:disabled {
    cursor: wait;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin: 0 20px 12px;
`;
